// Catálogo de posiciones de llanta por tipo de unidad — espejo de
// firebase/functions/src/tirePositions.mjs y src/core/tirePositions.ts
// (misma lista, mismo orden: los cambios se replican en los tres).

import type { UnitCategory, UnitKind } from './types';

/** Una posición física donde se monta una llanta (key estable + etiqueta). */
export interface TirePosition { key: string; axle: number; label: string }

// Vehículos 6x4: eje direccional (sencillas) + dos ejes de tracción (dobles).
const TRACTOR_POSITIONS: TirePosition[] = [
  { key: 'dir-l', axle: 1, label: 'Direccional izquierda' },
  { key: 'dir-r', axle: 1, label: 'Direccional derecha' },
  { key: 't1-le', axle: 2, label: 'Tracción 1 exterior izquierda' },
  { key: 't1-li', axle: 2, label: 'Tracción 1 interior izquierda' },
  { key: 't1-ri', axle: 2, label: 'Tracción 1 interior derecha' },
  { key: 't1-re', axle: 2, label: 'Tracción 1 exterior derecha' },
  { key: 't2-le', axle: 3, label: 'Tracción 2 exterior izquierda' },
  { key: 't2-li', axle: 3, label: 'Tracción 2 interior izquierda' },
  { key: 't2-ri', axle: 3, label: 'Tracción 2 interior derecha' },
  { key: 't2-re', axle: 3, label: 'Tracción 2 exterior derecha' },
];

// Camión recto / tortuga: eje direccional + un eje de tracción doble.
const TRUCK_POSITIONS: TirePosition[] = [
  { key: 'dir-l', axle: 1, label: 'Direccional izquierda' },
  { key: 'dir-r', axle: 1, label: 'Direccional derecha' },
  { key: 't-le', axle: 2, label: 'Tracción exterior izquierda' },
  { key: 't-li', axle: 2, label: 'Tracción interior izquierda' },
  { key: 't-ri', axle: 2, label: 'Tracción interior derecha' },
  { key: 't-re', axle: 2, label: 'Tracción exterior derecha' },
];

// Van / rabón ligero: cuatro sencillas.
const VAN_POSITIONS: TirePosition[] = [
  { key: 'del-l', axle: 1, label: 'Delantera izquierda' },
  { key: 'del-r', axle: 1, label: 'Delantera derecha' },
  { key: 'tra-l', axle: 2, label: 'Trasera izquierda' },
  { key: 'tra-r', axle: 2, label: 'Trasera derecha' },
];

// Remolque de 2 ejes con dobles (el lowboy de 3 ejes queda para configurar más
// adelante — hoy toma las dos traseras que casi siempre lleva en carretera).
const TRAILER_POSITIONS: TirePosition[] = [
  { key: 'e1-le', axle: 1, label: 'Eje 1 exterior izquierda' },
  { key: 'e1-li', axle: 1, label: 'Eje 1 interior izquierda' },
  { key: 'e1-ri', axle: 1, label: 'Eje 1 interior derecha' },
  { key: 'e1-re', axle: 1, label: 'Eje 1 exterior derecha' },
  { key: 'e2-le', axle: 2, label: 'Eje 2 exterior izquierda' },
  { key: 'e2-li', axle: 2, label: 'Eje 2 interior izquierda' },
  { key: 'e2-ri', axle: 2, label: 'Eje 2 interior derecha' },
  { key: 'e2-re', axle: 2, label: 'Eje 2 exterior derecha' },
];

const VAN_KINDS = new Set(['van']);
const TRUCK_KINDS = new Set(['camion']);

/** Posiciones montables del tipo de unidad; [] para reefer/equipo/custom sin llantas propias. */
export function tirePositionsForKind(kind: UnitKind, category: UnitCategory): TirePosition[] {
  if (category === 'vehicle') {
    if (VAN_KINDS.has(kind)) return VAN_POSITIONS;
    if (TRUCK_KINDS.has(kind)) return TRUCK_POSITIONS;
    return TRACTOR_POSITIONS; // tractor y tipos vehiculares personalizados
  }
  // Remolques y acoplados (category 'attachment') llevan sus propias llantas;
  // el equipo ('equipment': reefer, bomba, etc.) no.
  if (category === 'attachment') return TRAILER_POSITIONS;
  return [];
}

export function tirePositionLabel(kind: UnitKind, category: UnitCategory, key: string): string {
  return tirePositionsForKind(kind, category).find(p => p.key === key)?.label ?? key;
}
